/* eslint-disable max-lines */
import { COUNTRIES } from "../../constants/common/common";
import { plpPageConstants } from "../../constants/pageConstants/plpConstants";
import { isLocalMachine } from "../../utils/api/controlProxy";
import {
  parseMetaString,
  parseTwitterTags,
  removeEmptyNullAndUndefinedFromObject,
  withLatestLocation,
} from "../../utils/common";

const baseUrl = isLocalMachine() ? "/control" : "/d/control";

/**
 * Whether any PLP filter is applied (filtered listings also get the user's latest lat / long / accuracyType).
 * @param {string[]|string|undefined} filters
 * @returns {boolean}
 */
const hasAppliedFilters = (filters) => (Array.isArray(filters) ? filters.length > 0 : Boolean(filters));

/**
 *
 * @param {*} page
 * @param {*} size
 * @param {*} geoId
 * @param {*} categoryUrl
 * @param {*} isFromApp
 * @returns
 */
export const GET_PRODUCT_LISTING_URL = ({
  page = plpPageConstants?.INITIAL_PAGE,
  size = plpPageConstants?.DESKTOP_PRODUCT_PER_PAGE,
  geoId = COUNTRIES?.INDIA,
  categoryUrl,
  filters = undefined,
  sortBy = undefined,
  sortOrder = undefined,
  pageType = "",
  qs = "",
  pincode = "",
  email = "",
  fnpId = "",
}) => {
  const isSearch = pageType === plpPageConstants?.SEARCH;
  const trimmedEmail = typeof email === "string" ? email.trim() : "";

  // Build params using conditional spreads to minimize branching
  const queryParams = {
    page,
    size,
    geoId,
    pincode,
    qs,
    sortBy,
    sortOrder,
    ...(isSearch
      ? { pageType }
      : {
          categoryUrl,
          ...(trimmedEmail ? { email: trimmedEmail } : {}),
          ...(fnpId ? { fnpId } : {}),
        }),
    ...(hasAppliedFilters(filters) ? withLatestLocation() : {}),
  };

  // Remove empty, null, and undefined values from the params object
  const cleanedParams = removeEmptyNullAndUndefinedFromObject(queryParams);

  const urlParams = new URLSearchParams(cleanedParams);

  // Build filter parameters manually to use %20 instead of +
  let filterParams = "";
  if (filters && Array.isArray(filters)) {
    filterParams = filters.map((filter) => `filter=${encodeURIComponent(filter)}`).join("&");
  } else if (filters && typeof filters === "string") {
    filterParams = `filter=${encodeURIComponent(filters)}`;
  }

  const baseParams = urlParams.toString();
  const finalParams = filterParams ? `${baseParams}&${filterParams}` : baseParams;

  return `/search/v1/product-list?${finalParams}`;
};

/**
 * Builds the experiment resolve URL, which returns the campaign/segment/group a user is bucketed into.
 *
 * The product listing response carries the same fields, but that response is CDN-cached, so it can
 * hand back another user's bucket. This endpoint is therefore called from the browser only.
 *
 * @param {Object} params
 * @param {string} params.categoryUrl - Category url, e.g. `fnp.com/gifts/daughters-day-lp`.
 * @param {string} [params.geoId] - Catalog id.
 * @param {string} [params.email] - Logged-in user email.
 * @param {string} [params.fnpId] - Anonymous experiment id (`ab_user_id`).
 * @returns {string} Relative path for the search gateway.
 */
export const GET_PLP_EXPERIMENT_RESOLVE_URL = ({ categoryUrl, geoId = COUNTRIES?.INDIA, email = "", fnpId = "" }) => {
  const trimmedEmail = typeof email === "string" ? email.trim() : "";

  const queryParams = new URLSearchParams(
    removeEmptyNullAndUndefinedFromObject({
      geoId,
      categoryUrl,
      email: trimmedEmail,
      fnpId,
    }),
  );

  return `/search/v1/resolve?${queryParams.toString()}`;
};

/**
 *
 * @param {*} categoryUrl
 * @param {*} geoId
 * @returns
 */
export const GET_PLP_PRODUCT_FILTER_FOR_MOBILE = ({
  page = plpPageConstants?.INITIAL_PAGE,
  size = plpPageConstants?.DESKTOP_PRODUCT_PER_PAGE,
  geoId = COUNTRIES?.INDIA,
  categoryUrl,
  filters = undefined,
  sortBy = undefined,
  sortOrder = undefined,
  pageType = "",
  qs = "",
  pincode = "",
  email = "",
  fnpId = "",
}) => {
  const isSearch = pageType === plpPageConstants?.SEARCH;
  const trimmedEmail = typeof email === "string" ? email.trim() : "";

  // Build params using conditional spreads to minimize branching
  const queryParams = {
    page,
    size,
    geoId,
    pincode,
    qs,
    sortBy,
    sortOrder,
    ...(isSearch
      ? { pageType }
      : {
          categoryUrl,
          ...(trimmedEmail ? { email: trimmedEmail } : {}),
          ...(fnpId ? { fnpId } : {}),
        }),
    ...(hasAppliedFilters(filters) ? withLatestLocation() : {}),
  };

  // Remove empty, null, and undefined values from the params object
  const cleanedParams = removeEmptyNullAndUndefinedFromObject(queryParams);

  const urlParams = new URLSearchParams(cleanedParams);

  // Build filter parameters manually to use %20 instead of +
  let filterParams = "";
  if (filters && Array.isArray(filters)) {
    filterParams = filters.map((filter) => `filter=${encodeURIComponent(filter)}`).join("&");
  } else if (filters && typeof filters === "string") {
    filterParams = `filter=${encodeURIComponent(filters)}`;
  }

  const baseParams = urlParams.toString();
  const finalParams = filterParams ? `${baseParams}&${filterParams}` : baseParams;
  return `/search/v1/product-filters?${finalParams}`;
};

/**
 *
 * @param {*} productIDs
 * @returns
 */
export const GET_EARLIEST_DELIVERY_DATE_URL = (productIDs = "", pincode = "") => {
  return `${baseUrl}/getEarliestDeliveryDays?productIds=${productIDs}${pincode ? `&pincode=${pincode}` : ""}`;
};

/**
 * PLP serviceability earliest delivery URL.
 *
 * @param {Object} params
 * @param {string} [params.pincode]
 * @param {string} [params.pids] - Comma-separated product IDs
 * @param {number|string} [params.lat]
 * @param {number|string} [params.long]
 * @returns {string}
 */
export const GET_PLP_SERVICEABILITY_URL = ({ pincode = "", pids = "", lat, long } = {}) => {
  const queryParams = new URLSearchParams(
    removeEmptyNullAndUndefinedFromObject({
      pincode,
      pids,
      ...withLatestLocation({ lat, long }),
    }),
  );

  return `/serviceability/v1/plp?${queryParams.toString()}`;
};

/**
 * @param {*}
 * @returns
 */
export const GET_PRODUCT_ATTR_TAG_URL = () => {
  return `/chocolate/v1/global/static/product_attr_tag`;
};

/**
 * Microsite widget carousel — products by ID (search gateway).
 *
 * @param {string} productIds - Comma-separated product IDs (e.g. `COUR134883,EXP227`)
 * @returns {string} Relative path for `NEXT_PUBLIC_R2_CLIENT_API_GATEWAY`
 */
export const GET_WIDGET_PRODUCTS_URL = (productIds) => {
  return `/search/v1/products-by-ids?productIds=${productIds}`;
};

/** PDP search widget types sharing the same response schema. */
export const PDP_WIDGET_TYPES = {
  ALTERNATIVES: "alternatives",
  CVO: "cvo",
  TRENDING: "trending",
};

const PDP_WIDGET_API_VERSIONS = {
  [PDP_WIDGET_TYPES.ALTERNATIVES]: "v2",
  [PDP_WIDGET_TYPES.CVO]: "v1",
  [PDP_WIDGET_TYPES.TRENDING]: "v3",
};

/**
 * Unified PDP widget URL for alternatives, CVO, and trending APIs.
 *
 * @param {Object} params
 * @param {string} params.widgetType - alternatives | cvo | trending
 * @param {string} params.productId
 * @param {string} [params.pincode]
 * @param {string} [params.geo]
 * @param {number} [params.page] - trending only (0-based)
 * @param {number} [params.size] - trending only
 * @returns {string}
 */
export const GET_PDP_WIDGET_URL = ({
  widgetType,
  productId,
  pincode = "",
  geo = COUNTRIES.INDIA,
  page = 0,
  size = 40,
}) => {
  const version = PDP_WIDGET_API_VERSIONS[widgetType];
  const queryParams = new URLSearchParams({ geo });

  if (pincode) {
    queryParams.set("pincode", pincode);
  }

  if (widgetType === PDP_WIDGET_TYPES.TRENDING) {
    queryParams.set("page", String(page));
    queryParams.set("size", String(size));
  }

  return `/search/v1/widget/${version}/${widgetType}/${productId}?${queryParams.toString()}`;
};

/** @deprecated Use GET_PDP_WIDGET_URL with widgetType trending */
export const GET_TRENDING_PRODUCTS_URL = (params) =>
  GET_PDP_WIDGET_URL({ ...params, widgetType: PDP_WIDGET_TYPES.TRENDING });

/** @deprecated Use GET_PDP_WIDGET_URL with widgetType alternatives */
export const GET_WIDGET_ALTERNATIVES_URL = (params) =>
  GET_PDP_WIDGET_URL({ ...params, widgetType: PDP_WIDGET_TYPES.ALTERNATIVES });

/**
 * Returns the URL for fetching analytics data.
 *
 * @returns {string} The analytics data URL.
 */
export const PLP_HEAD_ANALYTICS = () => {
  return "/chocolate/v1/analytics/new-plp-headanalytics";
};

/**
 * Returns the URL for PLP batch CMS API.
 * Uses multiple paths query parameters: ?paths=/path1&paths=/path2&paths=/path3
 *
 * @param {string} categoryId - PLP category id (e.g. joined slugs)
 * @param {string[]} pathsArray - Array of paths to fetch
 * @returns {string} The batch API URL with multiple paths query parameters
 */
export const GET_CMS_BATCH_API_URL = (categoryId, pathsArray) => {
  const pathsQuery = pathsArray.map((path) => `paths=${path}`).join("&");
  return `/chocolate/v1/cms-batch/${categoryId}?${pathsQuery}`;
};

/**
 * Batch API path constants
 */
export const BATCH_API_PATHS = {
  ANALYTICS: "/analytics/new-plp-headanalytics",
  APP_REDIRECT_BANNER: "/global/static/web/mobile/fnp_app_redirect",
  FREE_DELIVERY_BANNER: "/global/static/web/freedelivery",
};

/**
 * TTL (ms) for PLP CMS batch in `withServerCache` on each Next.js Node process.
 * Higher values reduce origin load; lower values refresh CMS-backed data sooner.
 */
export const PLP_CMS_BATCH_SERVER_CACHE_TTL_MS = 600000;

/**
 * Builds all batch API paths in a single optimized call.
 * Menu is intentionally excluded — the mega menu is heavy (deep nested category
 * tree) and identical across every PLP/home render, so it's fetched client-side
 * once by <Header> instead of being re-fetched/re-parsed on every SSR request.
 *
 * @param {Object} params - Parameters for building paths
 * @param {boolean} params.isMobile - Whether the request is from mobile
 * @returns {string[]} Array of paths for batch API
 */
export const buildBatchApiPaths = ({ isMobile }) => {
  const paths = [
    // Always required paths
    BATCH_API_PATHS.ANALYTICS,
    BATCH_API_PATHS.FREE_DELIVERY_BANNER,
    // Mobile-only paths
    ...(isMobile ? [BATCH_API_PATHS.APP_REDIRECT_BANNER] : []),
  ].filter(Boolean); // Remove empty strings

  // Return array for multiple paths query parameters
  return paths;
};

const CMS_JSON_OBJECT_KEYS = ["relAltJson", "ogTagJson", "metaContentJson", "schemaTagJson", "customLinks"];

const EMPTY_META_CONTENT = { title: "", description: "", keywords: "" };

/**
 * Parses a CMS field that may already be an object/array or still a JSON string.
 * @param {unknown} value
 * @returns {unknown}
 */
const parseCmsJsonValue = (value) => {
  if (typeof value !== "string") {
    return value;
  }

  const trimmed = value.trim();
  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) {
    return value;
  }

  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
};

/**
 * Coerces migrated CMS JSON fields so page consumers can use object access.
 * @param {Object} metaTagsResponse
 * @returns {Object}
 */
export const normalizeMetaTagsContent = (metaTagsResponse) => {
  if (!metaTagsResponse || typeof metaTagsResponse !== "object") {
    return metaTagsResponse;
  }

  const normalized = { ...metaTagsResponse };
  CMS_JSON_OBJECT_KEYS.forEach((key) => {
    if (key in normalized) {
      normalized[key] = parseCmsJsonValue(normalized[key]);
    }
  });
  return normalized;
};

/**
 * Reduce relAlt array/object into flat `{ lang: url }` map.
 * @param {Array|Object} relAltData
 * @returns {Object}
 */
const buildLanguagesMap = (relAltData) => {
  const languages = {};
  const parsedRelAlt = parseCmsJsonValue(relAltData);

  if (Array.isArray(parsedRelAlt)) {
    parsedRelAlt.forEach((item) => {
      Object.entries(item || {}).forEach(([key, value]) => {
        languages[key] = value;
      });
    });
    return languages;
  }

  if (parsedRelAlt && typeof parsedRelAlt === "object") {
    Object.entries(parsedRelAlt).forEach(([key, value]) => {
      languages[key] = value;
    });
  }

  return languages;
};

/**
 * Resolves canonical href from `{ href }`, a plain URL, or legacy HTML.
 * @param {Object|string|null|undefined} canonical
 * @returns {string}
 */
const resolveCanonicalUrl = (canonical) => {
  if (!canonical) {
    return "";
  }

  if (typeof canonical === "object" && !Array.isArray(canonical)) {
    return canonical.href || canonical.url || "";
  }

  if (typeof canonical !== "string") {
    return "";
  }

  if (canonical.includes("<")) {
    return parseMetaString(canonical).canonical || "";
  }

  return canonical;
};

/**
 * Resolves Twitter card fields from an array, object, or legacy HTML string.
 * @param {Array|Object|string|null|undefined} twitterTag
 * @param {Object} fallbacks
 * @returns {Object}
 */
const resolveTwitterTags = (twitterTag, fallbacks) => {
  const parsedTwitter = parseCmsJsonValue(twitterTag);

  if (Array.isArray(parsedTwitter)) {
    const twitterByName = {};
    parsedTwitter.forEach((tag) => {
      if (tag?.name) {
        twitterByName[tag.name.replace("twitter:", "")] = tag.content ?? "";
      }
    });

    return {
      card: twitterByName.card || fallbacks.card,
      site: twitterByName.site || fallbacks.site,
      image: twitterByName.image || fallbacks.image,
      title: twitterByName.title || fallbacks.title,
      description: twitterByName.description || fallbacks.description,
    };
  }

  if (parsedTwitter && typeof parsedTwitter === "object") {
    return {
      card: parsedTwitter.card || parsedTwitter["twitter:card"] || fallbacks.card,
      site: parsedTwitter.site || parsedTwitter["twitter:site"] || fallbacks.site,
      image: parsedTwitter.image || parsedTwitter["twitter:image"] || fallbacks.image,
      title: parsedTwitter.title || parsedTwitter["twitter:title"] || fallbacks.title,
      description: parsedTwitter.description || parsedTwitter["twitter:description"] || fallbacks.description,
    };
  }

  if (typeof twitterTag === "string" && twitterTag) {
    return parseTwitterTags(twitterTag, fallbacks);
  }

  return fallbacks;
};

/**
 * Pre-parse all JSON-encoded fields in `metaTagsResponse` so consumers don't repeat work.
 * Result attached to mapped object as `parsed`. Safe to call on cached output.
 *
 * @param {Object} metaTagsResponse - raw metaTags content from CMS
 * @returns {Object} parsed bundle
 */
export const parseMetaTagsBundle = (metaTagsResponse) => {
  if (!metaTagsResponse) {
    return {
      footerLinks: {},
      scripts: [],
      preLoadArray: [],
      languages: {},
      metaContent: { ...EMPTY_META_CONTENT },
      canonicalUrl: "",
      twitterTags: null,
      metaParsed: null,
    };
  }

  const normalized = normalizeMetaTagsContent(metaTagsResponse);
  const footerLinks = normalized.footerLinks || {};
  const customLinks = normalized.customLinks || { scripts: [] };
  const scripts = Array.isArray(customLinks?.scripts) ? customLinks.scripts : [];
  let preLoadArray = [];
  if (Array.isArray(normalized.preLoad)) {
    preLoadArray = normalized.preLoad;
  } else if (Array.isArray(customLinks?.preloads)) {
    preLoadArray = customLinks.preloads;
  }

  const languages = buildLanguagesMap(normalized.relAltJson);
  const parsedMetaContent = parseCmsJsonValue(normalized.metaContentJson);
  const metaContent =
    parsedMetaContent && typeof parsedMetaContent === "object" && !Array.isArray(parsedMetaContent)
      ? parsedMetaContent
      : { ...EMPTY_META_CONTENT };

  const ogTagJson = parseCmsJsonValue(normalized.ogTagJson) || {};
  const canonicalUrl = resolveCanonicalUrl(normalized.canonical);
  const twitterTags = resolveTwitterTags(normalized.twitterTag, {
    card: "summary_large_image",
    site: "",
    image: ogTagJson.image || "",
    title: ogTagJson.title || metaContent.title || "",
    description: ogTagJson.description || metaContent.description || "",
  });

  return {
    footerLinks,
    scripts,
    preLoadArray,
    languages,
    metaContent,
    canonicalUrl,
    twitterTags,
    metaParsed: null,
  };
};

/**
 * Maps batch API response to expected format. Pre-parses JSON-encoded fields so the
 * page route does not repeat parse work each render.
 *
 * @param {Object} batchResponse - The batch API response
 * @param {Object} params - Parameters for mapping
 * @param {boolean} params.isMobile - Whether the request is from mobile
 * @param {string} params.categoryId - The category ID
 * @param {string} params.categoryIdForReviews - The category ID for reviews (without -lp)
 * @returns {Object} Mapped response object with `parsed` bundle
 */
export const mapBatchApiResponse = (batchResponse, { isMobile, categoryId, categoryIdForReviews }) => {
  const metaTagsPath = categoryId && categoryId !== "" && categoryId !== "undefined" ? `/category/${categoryId}` : "";
  const reviewsPath =
    categoryIdForReviews && categoryIdForReviews !== "" && categoryIdForReviews !== "undefined"
      ? `/moody/v1/category/reviews`
      : "";

  // Extract category data - supports both old and new structure
  const categoryData = metaTagsPath && batchResponse[metaTagsPath] ? batchResponse[metaTagsPath] : {};

  // New structure: content and reviews are nested under category path
  // Old structure: content is directly under path, reviews are separate
  const metaTagsResponse = normalizeMetaTagsContent(
    categoryData?.content ||
      (metaTagsPath && batchResponse[metaTagsPath]?.content ? batchResponse[metaTagsPath].content : {}),
  );
  const productReviews =
    categoryData?.reviews ||
    (reviewsPath && batchResponse[reviewsPath]?.content ? batchResponse[reviewsPath].content : {});

  const parsed = parseMetaTagsBundle(metaTagsResponse);

  return {
    plpHeadAnalyticsResponse: batchResponse[BATCH_API_PATHS.ANALYTICS]?.content || {},
    metaTagsResponse,
    productReviews,
    // App redirect banner only for mobile (msite related)
    appRedirectBannerResponse:
      isMobile && batchResponse[BATCH_API_PATHS.APP_REDIRECT_BANNER]?.content
        ? batchResponse[BATCH_API_PATHS.APP_REDIRECT_BANNER].content
        : {},
    freeBannerData: batchResponse[BATCH_API_PATHS.FREE_DELIVERY_BANNER]?.content || null,
    parsed,
  };
};
